import { useState, ChangeEvent, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  SelectChangeEvent,
  CircularProgress,
  Box,
} from "@mui/material";
import { useCreateStrategyMutation, useListStrategiesQuery, useListAssetsQuery } from "features/trading/accounts";

interface CreateStrategyModalProps {
  open: boolean;
  onClose: () => void;
  strategyGroupId: number;
}

interface StrategyFormData {
  name: string;
  asset: number | "";
  strategy_group: number;
  current_call: number | null;
  previous_call: number | null;
  current_call_price: string;
  hedge_ratio: string;
  target_delta: string;
}

const CreateStrategyModal: React.FC<CreateStrategyModalProps> = ({
  open,
  onClose,
  strategyGroupId,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState<StrategyFormData>({
    name: "",
    asset: "",
    strategy_group: strategyGroupId,
    current_call: null,
    previous_call: null,
    current_call_price: "0",
    hedge_ratio: "0.80",
    target_delta: "0.30",
  });

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      strategy_group: strategyGroupId
    }));
  }, [strategyGroupId]);

  const [createStrategy] = useCreateStrategyMutation();
  const { data: strategiesData } = useListStrategiesQuery({
    pagination: { page: 0, pageSize: 1 },
    filters: { strategy_group: strategyGroupId },
  });
  const { data: assetsData, isLoading: isAssetsLoading } = useListAssetsQuery({
    search: searchTerm,
  });

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClose = () => {
    setFormData({
      name: "",
      asset: "",
      strategy_group: strategyGroupId,
      current_call: null,
      previous_call: null,
      current_call_price: "0",
      hedge_ratio: "0.80",
      target_delta: "0.30",
    });
    setSearchTerm("");
    onClose();
  };

  const handleInputChange = (field: keyof StrategyFormData) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (field: keyof StrategyFormData) => (
    event: SelectChangeEvent<number>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const firstStrategy = strategiesData?.results?.[0];
      let call_or_put = false;
      if (firstStrategy) {
        call_or_put = firstStrategy.call_or_put;
      }
      else {
        if(strategyGroupId === 4) {
          call_or_put = true;
        }
        else {
          call_or_put = false;
        }
      }
      await createStrategy({
        ...formData,
        asset: Number(formData.asset),
        current_call_price: formData.current_call_price,
        call_or_put: call_or_put,
        strategy_group: strategyGroupId,
      }).unwrap();
      handleClose();
    } catch (error) {
      console.error('Failed to create strategy:', error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Position</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                label="Position Name"
                type="text"
                fullWidth
                value={formData.name}
                onChange={handleInputChange("name")}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box mb={1}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search symbol..."
                  label="Search symbol..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Box>
              {searchTerm ?
                <FormControl fullWidth>
                <InputLabel>Asset</InputLabel>
                <Select
                  value={formData.asset}
                  label="Asset"
                  onChange={handleSelectChange("asset")}
                  required
                >
                  {isAssetsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : assetsData?.results?.length === 0 ? (
                    <MenuItem disabled>No assets found</MenuItem>
                  ) : (
                    assetsData?.results?.map((asset) => (
                      <MenuItem key={asset.id} value={asset.id}>
                        {asset.symbol} - {asset.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              :
              <></>
              }
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label="Hedge Ratio"
                type="number"
                fullWidth
                value={formData.hedge_ratio}
                onChange={handleInputChange("hedge_ratio")}
                inputProps={{ step: 0.01, min: 0, max: 1 }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label="Target Delta"
                type="number"
                fullWidth
                value={formData.target_delta}
                onChange={handleInputChange("target_delta")}
                inputProps={{ step: 0.01, min: 0, max: 1 }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!formData.name.trim() || !formData.asset}
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateStrategyModal;
